import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ChefHat, AlertTriangle, AlertCircle, Info, Shield, Trash2, Wrench, RefreshCw, Filter, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SEVERITY_CONFIG = {
  CRITICAL: { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle, label: 'Critical' },
  MEDIUM: { color: 'bg-amber-100 text-amber-800 border-amber-200', icon: AlertCircle, label: 'Medium' },
  LOW: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Info, label: 'Low' },
  NONE: { color: 'bg-green-100 text-green-800 border-green-200', icon: Shield, label: 'Clean' },
};

const ISSUE_LABELS = {
  self_translation: 'Self-Translation',
  wrong_translation_language: 'Wrong Translation Language',
  source_mismatch: 'Source Mismatch',
  failed_translation: 'Failed Translation',
  fallback_translation: 'Fallback Translation',
  low_confidence: 'Low Confidence',
};

const ISSUE_SEVERITY_MAP = {
  self_translation: 'CRITICAL',
  wrong_translation_language: 'CRITICAL',
  source_mismatch: 'MEDIUM',
  failed_translation: 'LOW',
  fallback_translation: 'LOW',
  low_confidence: 'LOW',
};

export default function AdminLanguageIntegrityPage() {
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [issuesOnly, setIssuesOnly] = useState(true);
  const [issueFilter, setIssueFilter] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const navigate = useNavigate();

  const getHeaders = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return {}; }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '200', has_issues_only: issuesOnly });
      if (issueFilter) params.append('issue_filter', issueFilter);
      const { data } = await axios.get(
        `${API_URL}/api/admin/language-integrity/audit?${params}`,
        { headers: getHeaders() }
      );
      setResults(data.results || []);
      setSummary({
        total_recipes: data.total_recipes,
        total_with_issues: data.total_with_issues,
        issue_type_counts: data.issue_type_counts || {},
        severity_counts: data.severity_counts || {},
      });
    } catch (err) {
      if (err.response?.status === 401) navigate('/admin/login');
      else toast.error('Failed to load audit data');
    } finally {
      setLoading(false);
    }
  }, [issuesOnly, issueFilter]);

  useEffect(() => { fetchAudit(); }, [fetchAudit]);

  const runAction = async (slug, action, label) => {
    setActionLoading(prev => ({ ...prev, [`${slug}-${action}`]: true }));
    try {
      await axios.post(
        `${API_URL}/api/admin/language-integrity/${action}/${slug}?dry_run=0`,
        {},
        { headers: getHeaders() }
      );
      toast.success(`${label} applied to ${slug}`);
      fetchAudit();
    } catch (err) {
      toast.error(`Action failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setActionLoading(prev => ({ ...prev, [`${slug}-${action}`]: false }));
    }
  };

  const SeverityBadge = ({ severity }) => {
    const cfg = SEVERITY_CONFIG[severity] || SEVERITY_CONFIG.NONE;
    const Icon = cfg.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${cfg.color}`} data-testid={`severity-${severity?.toLowerCase()}`}>
        <Icon className="w-3 h-3" />
        {cfg.label}
      </span>
    );
  };

  const IssueBadge = ({ issue }) => {
    const sev = ISSUE_SEVERITY_MAP[issue] || 'LOW';
    const cfg = SEVERITY_CONFIG[sev];
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${cfg.color}`} data-testid={`issue-badge-${issue}`}>
        {ISSUE_LABELS[issue] || issue}
      </span>
    );
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Language Integrity - Admin</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50" data-testid="language-integrity-page">
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/admin/recipes" className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <ChefHat className="w-7 h-7 text-amber-700" />
              <h1 className="text-lg font-bold text-gray-800">Language Integrity Audit</h1>
            </div>
            <Button variant="outline" size="sm" onClick={fetchAudit} disabled={loading} data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
          {/* Summary cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="summary-cards">
              <div className="bg-white rounded-lg border p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-gray-900">{summary.total_recipes}</div>
                <div className="text-xs text-gray-500 mt-1">Recipes Scanned</div>
              </div>
              <div className="bg-white rounded-lg border p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-red-600">{summary.total_with_issues}</div>
                <div className="text-xs text-gray-500 mt-1">With Issues</div>
              </div>
              <div className="bg-white rounded-lg border p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-red-700">{summary.severity_counts?.CRITICAL || 0}</div>
                <div className="text-xs text-gray-500 mt-1">Critical</div>
              </div>
              <div className="bg-white rounded-lg border p-4 text-center shadow-sm">
                <div className="text-2xl font-bold text-amber-600">{summary.severity_counts?.MEDIUM || 0}</div>
                <div className="text-xs text-gray-500 mt-1">Medium</div>
              </div>
            </div>
          )}

          {/* Issue type breakdown */}
          {summary?.issue_type_counts && Object.keys(summary.issue_type_counts).length > 0 && (
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="px-4 py-2 border-b"><h3 className="text-sm font-semibold text-gray-800">Issue Breakdown</h3></div>
              <div className="px-4 py-3">
                <div className="flex flex-wrap gap-2" data-testid="issue-breakdown">
                  {Object.entries(summary.issue_type_counts).map(([type, count]) => (
                    <button
                      key={type}
                      onClick={() => setIssueFilter(issueFilter === type ? '' : type)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer ${
                        issueFilter === type
                          ? 'bg-gray-800 text-white border-gray-800'
                          : `${SEVERITY_CONFIG[ISSUE_SEVERITY_MAP[type] || 'LOW'].color} hover:opacity-80`
                      }`}
                      data-testid={`filter-${type}`}
                    >
                      {ISSUE_LABELS[type] || type}: {count}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-3" data-testid="filters">
            <Button
              variant={issuesOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setIssuesOnly(!issuesOnly)}
              data-testid="toggle-issues-only"
            >
              <Filter className="w-4 h-4 mr-1" />
              {issuesOnly ? 'Showing Issues Only' : 'Showing All'}
            </Button>
            {issueFilter && (
              <Badge variant="secondary" className="cursor-pointer" onClick={() => setIssueFilter('')}>
                Filter: {ISSUE_LABELS[issueFilter] || issueFilter} &times;
              </Badge>
            )}
            <span className="text-sm text-gray-500">{results.length} recipe(s)</span>
          </div>

          {/* Results table */}
          {loading ? (
            <div className="text-center py-12 text-gray-500" data-testid="loading-state">Loading audit data...</div>
          ) : results.length === 0 ? (
            <div className="text-center py-12 text-gray-400" data-testid="empty-state">
              {issuesOnly ? 'No issues found — all recipes are clean.' : 'No recipes found.'}
            </div>
          ) : (
            <div className="space-y-3" data-testid="results-list">
              {results.map(r => (
                <div key={r.slug} className="bg-white rounded-lg border shadow-sm overflow-hidden" data-testid={`recipe-row-${r.slug}`}>
                  <div className="px-4 py-3">
                    {/* Header row */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900 text-sm truncate" data-testid="recipe-name">{r.recipe_name || r.slug}</span>
                          <SeverityBadge severity={r.severity} />
                        </div>
                        <div className="text-xs text-gray-500 mt-1 font-mono" data-testid="recipe-slug">{r.slug}</div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          content_language: <span className="font-medium">{r.content_language || '—'}</span>
                          {' / '}
                          detected: <span className="font-medium">{r.detected_source_language}</span>
                          {' '}
                          (conf: {r.language_confidence})
                        </div>
                      </div>
                    </div>

                    {/* Issues */}
                    {r.issues?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2" data-testid="recipe-issues">
                        {r.issues.map(iss => <IssueBadge key={iss} issue={iss} />)}
                      </div>
                    )}

                    {/* Translation details */}
                    {r.translations && Object.keys(r.translations).length > 0 && (
                      <div className="mt-2 border-t pt-2">
                        <div className="text-xs font-medium text-gray-600 mb-1">Translations:</div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5">
                          {Object.entries(r.translations).map(([lang, t]) => (
                            <div key={lang} className={`text-xs px-2 py-1 rounded border ${
                              t.issues?.length ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
                            }`} data-testid={`translation-${lang}`}>
                              <span className="font-mono font-medium">{lang}</span>
                              {' → '}
                              <span>detected: {t.detected_language}</span>
                              {' '}
                              <span className="text-gray-400">({t.status})</span>
                              {t.issues?.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-0.5">
                                  {t.issues.map(i => (
                                    <span key={i} className="px-1 py-0 bg-red-100 text-red-700 rounded text-[10px]">{i}</span>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    {r.issues?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t" data-testid="recipe-actions">
                        {r.issues.includes('source_mismatch') && (
                          <Button
                            variant="outline" size="sm"
                            className="text-xs h-7 border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => runAction(r.slug, 'fix-content-language', 'Fix content_language')}
                            disabled={actionLoading[`${r.slug}-fix-content-language`]}
                            data-testid={`action-fix-lang-${r.slug}`}
                          >
                            <Wrench className="w-3 h-3 mr-1" />
                            Fix content_language
                          </Button>
                        )}
                        {(r.issues.includes('wrong_translation_language') || r.issues.includes('self_translation')) && (
                          <Button
                            variant="outline" size="sm"
                            className="text-xs h-7 border-red-300 text-red-700 hover:bg-red-50"
                            onClick={() => runAction(r.slug, 'delete-invalid-translations', 'Delete invalid translations')}
                            disabled={actionLoading[`${r.slug}-delete-invalid-translations`]}
                            data-testid={`action-delete-invalid-${r.slug}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete Invalid Translations
                          </Button>
                        )}
                        <Button
                          variant="ghost" size="sm"
                          className="text-xs h-7 text-gray-500 hover:text-red-600"
                          onClick={() => runAction(r.slug, 'invalidate-all-translations', 'Invalidate all translations')}
                          disabled={actionLoading[`${r.slug}-invalidate-all-translations`]}
                          data-testid={`action-invalidate-all-${r.slug}`}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Clear All Translations
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
